import {HeadingNode} from '@lexical/rich-text';
import {ExcludeWordcountNode} from 'components/DSYS/editor/nodes/ExcludeWordcountNode';
import {PlaceholderNode} from 'components/DSYS/editor/nodes/PlaceholderNode';
import {
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $setSelection,
  LexicalEditor,
  type LexicalNode,
  NodeKey,
  TextNode,
} from 'lexical';
import {EnhancedTableCellNode} from 'plugins/enhancedTableCells';

import {EnhancedHeadingNode} from './EnhancedHeadingNode';

export interface SectionRange {
  headingKey: NodeKey;
  contentKeys: NodeKey[];
  endKey?: NodeKey;
}

/**
 * Get the heading level (1-6) from a heading node
 */
export function getHeadingLevel(node: HeadingNode): number {
  const tag = node.getTag();
  return parseInt(tag.replace('h', ''), 10);
}

/**
 * Compare two heading levels
 * @returns -1 if a < b, 0 if a === b, 1 if a > b
 */
export function compareLevels(aTag: string, bTag: string): number {
  const aLevel = parseInt(aTag.replace('h', ''), 10);
  const bLevel = parseInt(bTag.replace('h', ''), 10);

  if (aLevel < bLevel) return -1;
  if (aLevel > bLevel) return 1;
  return 0;
}

/**
 * Find the section range for a given heading node
 * Returns all sibling nodes that belong to this heading's section
 */
export function findSectionRangeForHeading(headingNode: HeadingNode): SectionRange {
  const headingKey = headingNode.getKey();
  const headingLevel = getHeadingLevel(headingNode);
  const contentKeys: NodeKey[] = [];

  // Get the parent container
  const parent = headingNode.getParent();
  if (!parent || !$isElementNode(parent)) {
    return {headingKey, contentKeys};
  }

  const siblings = parent.getChildren();
  const headingIndex = siblings.findIndex((child) => child.getKey() === headingKey);

  if (headingIndex === -1) {
    return {headingKey, contentKeys};
  }

  // Look for content nodes after this heading until we hit a heading of equal or higher level
  for (let i = headingIndex + 1; i < siblings.length; i++) {
    const sibling = siblings[i];

    if (sibling instanceof HeadingNode) {
      const siblingLevel = getHeadingLevel(sibling);
      // If we hit a heading of equal or higher level, this section ends
      if (siblingLevel <= headingLevel) {
        break;
      }
    }

    // This node belongs to our section
    contentKeys.push(sibling.getKey());
  }

  return {
    headingKey,
    contentKeys,
    endKey: contentKeys.length > 0 ? contentKeys[contentKeys.length - 1] : undefined,
  };
}

/**
 * Check if a node or any of its ancestors is a PlaceholderNode, ExcludeWordcountNode or HeadingNode
 */
function isExcludedNode(node: LexicalNode | null): boolean {
  let currentNode = node;
  while (currentNode) {
    if (
      currentNode instanceof PlaceholderNode ||
      currentNode instanceof ExcludeWordcountNode ||
      currentNode instanceof EnhancedTableCellNode ||
      currentNode instanceof EnhancedHeadingNode
    ) {
      return true;
    }
    currentNode = currentNode.getParent();
  }
  return false;
}

/**
 * Get plain text content for a heading's section, excluding placeholder and nested heading nodes
 */
export function getPlainTextForSection(editor: LexicalEditor, headingNode: HeadingNode): string {
  let text = '';

  editor.getEditorState().read(() => {
    const range = findSectionRangeForHeading(headingNode);

    // Don't add the heading text itself - we only want content under it

    // Add content from all nodes in the section, excluding placeholders and nested headings
    range.contentKeys.forEach((key) => {
      const node = $getNodeByKey(key);
      if (node && $isElementNode(node)) {
        // Get all text nodes within this node
        const textNodes = node.getAllTextNodes();
        // Get all typeahead nodes within this node
        const typeaheadNodes = getAllTypeaheadNodes(node);
        [...textNodes, ...typeaheadNodes].forEach((textNode) => {
          // Only include text if it's not inside a placeholder or heading
          if (!isExcludedNode(textNode)) {
            text += textNode.getTextContent() + ' ';
          }
        });
      }
    });
  });

  return text;
}

/**
 * Clamp selection to prevent cursor navigation into hidden blocks
 */
export function clampSelectionOutOfHidden(editor: LexicalEditor, hiddenKeys: Set<NodeKey>): void {
  editor.getEditorState().read(() => {
    const selection = $getSelection();

    if (!$isRangeSelection(selection)) {
      return;
    }

    const anchorNode = selection.anchor.getNode();
    const focusNode = selection.focus.getNode();

    // Check if selection is in hidden content
    const isAnchorHidden =
      hiddenKeys.has(anchorNode.getKey()) || hiddenKeys.has(anchorNode.getParent()?.getKey() || '');
    const isFocusHidden =
      hiddenKeys.has(focusNode.getKey()) || hiddenKeys.has(focusNode.getParent()?.getKey() || '');

    if (isAnchorHidden || isFocusHidden) {
      // Find the nearest visible heading to move selection to
      const root = $getRoot();
      const allNodes = root.getAllTextNodes();

      for (const textNode of allNodes) {
        const parent = textNode.getParent();
        if (parent && parent instanceof HeadingNode && !hiddenKeys.has(parent.getKey())) {
          // Move selection to end of this heading
          const newSelection = selection.clone();
          newSelection.anchor.set(parent.getKey(), parent.getChildrenSize(), 'element');
          newSelection.focus.set(parent.getKey(), parent.getChildrenSize(), 'element');
          $setSelection(newSelection);
          break;
        }
      }
    }
  });
}

/**
 * Get all heading nodes in the document
 */
export function getAllHeadings(editor: LexicalEditor): HeadingNode[] {
  const headings: HeadingNode[] = [];

  editor.getEditorState().read(() => {
    const root = $getRoot();
    const allNodes = root.getAllTextNodes();

    for (const textNode of allNodes) {
      const parent = textNode.getParent();
      if (parent && parent instanceof HeadingNode && !headings.includes(parent)) {
        headings.push(parent);
      }
    }
  });

  return headings;
}

/**
 * Find the next heading at a specific level or higher
 */
export function findNextHeadingAtLevel(
  editor: LexicalEditor,
  fromHeading: HeadingNode,
  targetLevel: number
): HeadingNode | null {
  let nextHeading: HeadingNode | null = null;

  editor.getEditorState().read(() => {
    const root = $getRoot();
    const allNodes = root.getAllTextNodes();
    const fromKey = fromHeading.getKey();
    let foundFromHeading = false;

    for (const textNode of allNodes) {
      const parent = textNode.getParent();
      if (parent && parent instanceof HeadingNode) {
        if (parent.getKey() === fromKey) {
          foundFromHeading = true;
          continue;
        }

        if (foundFromHeading) {
          const level = getHeadingLevel(parent);
          if (level <= targetLevel) {
            nextHeading = parent;
            break;
          }
        }
      }
    }
  });

  return nextHeading;
}

/**
 * Count words in a text string
 */
export function countWords(text: string): number {
  if (text.trim() === '') {
    return 0;
  }
  return text
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length;
}

/**
 * Get word count for a heading's section
 */
export function getSectionWordCount(editor: LexicalEditor, headingNode: HeadingNode): number {
  const text = getPlainTextForSection(editor, headingNode);
  return countWords(text);
}

/**
 * Find which heading a given node belongs to
 * Returns the heading node that contains this node in its section
 */
export function findHeadingForNode(editor: LexicalEditor, nodeKey: NodeKey): HeadingNode | null {
  let headingNode: HeadingNode | null = null;

  editor.getEditorState().read(() => {
    const root = $getRoot();
    const allNodes = root.getAllTextNodes();
    const targetNode = $getNodeByKey(nodeKey);

    if (!targetNode) return;

    // Find all headings and their positions
    const headings: {node: HeadingNode; index: number}[] = [];

    for (let i = 0; i < allNodes.length; i++) {
      const textNode = allNodes[i];
      const parent = textNode.getParent();
      if (
        parent &&
        parent instanceof HeadingNode &&
        !headings.some((h) => h.node.getKey() === parent.getKey())
      ) {
        headings.push({node: parent, index: i});
      }
    }

    // Find the target node's position
    let targetIndex = -1;
    for (let i = 0; i < allNodes.length; i++) {
      if (allNodes[i].getKey() === nodeKey) {
        targetIndex = i;
        break;
      }
    }

    if (targetIndex === -1) return;

    // Find the last heading before the target node
    let lastHeading: HeadingNode | null = null;
    for (const heading of headings) {
      if (heading.index < targetIndex) {
        lastHeading = heading.node;
      } else {
        break;
      }
    }

    headingNode = lastHeading;
  });

  return headingNode;
}

/**
 * Get all text nodes within a specific heading section
 * This is used by the AI plugin to scope suggestions to only the current section
 */
export function getTextNodesInSection(editor: LexicalEditor, headingNode: HeadingNode): TextNode[] {
  const textNodes: TextNode[] = [];

  editor.getEditorState().read(() => {
    const root = $getRoot();
    const allTextNodes = root.getAllTextNodes();

    // Find the heading's position in the document
    const headingKey = headingNode.getKey();
    let headingIndex = -1;

    for (let i = 0; i < allTextNodes.length; i++) {
      const textNode = allTextNodes[i];
      const parent = textNode.getParent();
      if (parent && parent.getKey() === headingKey) {
        headingIndex = i;
        break;
      }
    }

    if (headingIndex === -1) return;

    // Find the next heading at the same or higher level
    const headingLevel = getHeadingLevel(headingNode);
    let endIndex = allTextNodes.length;

    for (let i = headingIndex + 1; i < allTextNodes.length; i++) {
      const textNode = allTextNodes[i];
      const parent = textNode.getParent();
      if (parent && parent instanceof HeadingNode) {
        const parentLevel = getHeadingLevel(parent);
        if (parentLevel <= headingLevel) {
          endIndex = i;
          break;
        }
      }
    }

    // Collect all text nodes in this section
    for (let i = headingIndex; i < endIndex; i++) {
      const textNode = allTextNodes[i];
      const parent = textNode.getParent();

      // Skip the heading text itself, only include content under it
      if (parent && parent.getKey() !== headingKey) {
        textNodes.push(textNode);
      }
    }
  });

  return textNodes;
}

const TYPEAHEAD_NODE_TYPE = 'typeahead';

export function getAllTypeaheadNodes(root: LexicalNode): LexicalNode[] {
  const result: LexicalNode[] = [];

  function traverse(node: LexicalNode) {
    if (node.getType() === TYPEAHEAD_NODE_TYPE) {
      result.push(node);
      return;
    }

    if ($isElementNode(node)) {
      node.getChildren().forEach(traverse);
    }
  }

  traverse(root);
  return result;
}
